import { Box, ButtonBase } from "@mui/material";

export interface ChoiceOption {
  value: string | number;
  label: string;
}

export type ChoiceToggleSize = "default" | "small";

interface Props {
  value: string | number;
  onChange: (value: string | number) => void;
  options: ChoiceOption[];
  disabled?: boolean;
  /**
   * `default` reparte el ancho disponible entre las opciones; `small` las ajusta al texto,
   * para cuando el toggle acompaña a otro contenido en lugar de ocupar la fila completa.
   */
  size?: ChoiceToggleSize;
}

const SIZES = {
  default: { flex: 1, padding: "14px 18px", fontSize: 14 },
  small: { flex: "0 0 auto", padding: "14px 18px", fontSize: 14 },
} as const;

/**
 * Replica `.choice-chip` de styles.css: botones rectangulares de ancho igual
 * para preguntas de opción excluyente (Sí/No, plazo, etc.).
 */
export const ChoiceToggle = ({ value, onChange, options, disabled = false, size = "default" }: Props) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: size === "small" ? "wrap" : "nowrap" }}>
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <ButtonBase
          key={opt.value}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          sx={{
            flex: SIZES[size].flex,
            justifyContent: "center",
            border: "1.5px solid",
            borderColor: selected ? "primary.main" : "divider",
            borderRadius: "8px",
            padding: SIZES[size].padding,
            fontSize: SIZES[size].fontSize,
            fontWeight: selected ? 600 : 500,
            color: "primary.dark",
            bgcolor: selected ? "primary.base" : "background.paper",
            boxShadow: selected ? (theme) => `0 0 0 1px ${theme.palette.primary.main} inset` : "none",
            transition: "border-color .15s ease, background-color .15s ease, color .15s ease",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.base",
              color: selected ? "primary.dark" : "primary.accent",
            },
            // Deshabilitado: la opción elegida sigue siendo legible pero atenuada,
            // el resto toma el mismo fondo gris que los inputs `lendia_input`.
            "&.Mui-disabled": {
              borderColor: selected ? "primary.main" : "divider",
              bgcolor: selected ? "primary.base" : "tokens.surface2",
              color: selected ? "primary.dark" : "text.disabled",
              opacity: selected ? 0.6 : 1,
              boxShadow: selected ? (theme) => `0 0 0 1px ${theme.palette.primary.main} inset` : "none",
            },
          }}
        >
          {opt.label}
        </ButtonBase>
      );
    })}
  </Box>
);
